import { navigate } from "@reach/router";
import React, { createContext, useContext, useReducer } from "react";
import VideosReducer from "../reducers/VideosReducer";
import AdjuntosService from "../services/AdjuntosService";
import VideosService from "../services/VideosService";
import {
  VIDEOS_RECIBIDOS,
  SINGLE_VIDEO_RECIBIDO,
  SET_PROPIEDAD_VIDEO,
  CREATE_VIDEO,
  PURCHASE_NEEDED,
  SHOW_SPINNER,
  HIDE_SPINNER,
  FAVORITOS_RECIBIDOS,
  RELOAD_VIDEOS,
  CLEAR_RELOAD,
} from "../types";
import { hideModal } from "../utils";
import { ModalContext } from "./ModalContext";

const initialState = {
  favoritos: null,
  spinner: false,
  videos: null,
  video: null,
};

export const VideosContext = createContext(initialState);

export const VideosProvider = ({ children }) => {
  const [state, dispatch] = useReducer(VideosReducer, initialState);

  const { alert, success } = useContext(ModalContext);

  const getVideos = (filters) => {
    dispatch({ type: SHOW_SPINNER });
    VideosService.getVideos(filters)
      .then((res) => {
        dispatch({ type: HIDE_SPINNER });
        const { videos } = res.data;
        dispatch({ type: VIDEOS_RECIBIDOS, payload: videos });
      })
      .catch((error) => {
        dispatch({ type: HIDE_SPINNER });
        if (error.response) {
          if (error.response.status === 412) {
            dispatch({ type: PURCHASE_NEEDED });
            dispatch({ type: VIDEOS_RECIBIDOS, payload: [] });
          }
        }
      });
  };

  const getFavoritos = (filters) => {
    dispatch({ type: SHOW_SPINNER });
    VideosService.getFavoritos(filters)
      .then((res) => {
        const { favorites } = res.data;
        dispatch({ type: HIDE_SPINNER });
        dispatch({ type: FAVORITOS_RECIBIDOS, payload: favorites });
      })
      .catch((error) => {
        dispatch({ type: HIDE_SPINNER });
        alert("Hubo un error al obtener tus favoritos.");
      });
  };

  const clearFavoritos = () => {
    dispatch({ type: FAVORITOS_RECIBIDOS, payload: null });
  };

  const getAllVideos = (query) => {
    VideosService.getAllVideos(query).then((res) => {
      const { videos } = res.data;
      dispatch({ type: VIDEOS_RECIBIDOS, payload: videos });
    });
  };

  const getSingleVideo = (video_id) => {
    VideosService.getSingleVideo(video_id)
      .then((res) => {
        const { video } = res.data;
        dispatch({ type: SINGLE_VIDEO_RECIBIDO, payload: video });
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status === 404) {
            alert("Lo sentimos, no hemos encontrado este video.");
            navigate("/mylatina");
          } else if (error.response.status === 402) {
            alert("Lo sentimos, no tienes acceso a este video.");
            window.open("https://athome.latinatribe.mx/mylatina/shop", "_self");
          }
        }
      });
  };

  const agregarFavorito = (video_id, callback) => {
    VideosService.postFavorito(video_id).then(() => {
      if (typeof callback === "function") {
        callback();
      }
      dispatch({ type: RELOAD_VIDEOS });
      success("¡Favorito agregado!");
    });
  };

  const eliminarFavorito = (video_id, callback) => {
    VideosService.deleteFavorito(video_id).then(() => {
      if (typeof callback === "function") {
        callback();
      }
      dispatch({ type: RELOAD_VIDEOS });
      success("¡Favorito eliminado!");
    });
  };

  const deleteVideo = (video_id) => {
    VideosService.deleteVideo(video_id).then(() => {
      navigate("/mylatina/admin/videos");
      success("¡Video eliminado con éxito!");
      hideModal();
      getAllVideos();
    });
  };

  const setPropiedadVideo = (key, value) => {
    dispatch({ type: SET_PROPIEDAD_VIDEO, payload: { key, value } });
  };

  const clearVideos = () => {
    dispatch({ type: VIDEOS_RECIBIDOS, payload: null });
  };

  const createVideo = () => {
    dispatch({ type: CREATE_VIDEO });
  };

  const postVideo = (video) => {
    const callback = () => {
      dispatch({ type: HIDE_SPINNER });
      navigate("/mylatina/admin/videos");
      success("Video guardado con éxito");
    };
    const handleError = () => {
      dispatch({ type: HIDE_SPINNER });
      alert("Hubo un error al subir el video. Vuelve a intentarlo.");
    };
    if (video.file && video.file !== null) {
      const formData = new FormData();
      formData.append("adjunto", video.file);
      dispatch({ type: SHOW_SPINNER });
      AdjuntosService.postAdjunto(formData)
        .then((res) => {
          const { idAdjunto } = res.data;
          video.thumbnail = idAdjunto;
          if (isNaN(video.video_id)) {
            VideosService.postVideo(video).then(callback).catch(handleError);
          } else {
            VideosService.putVideo(video).then(callback).catch(handleError);
          }
        })
        .catch((error) => {
          dispatch({ type: HIDE_SPINNER });
        });
    } else {
      dispatch({ type: SHOW_SPINNER });
      if (isNaN(video.video_id)) {
        VideosService.postVideo(video).then(callback).catch(handleError);
      } else {
        VideosService.putVideo(video).then(callback).catch(handleError);
      }
    }
  };

  const clearReload = () => {
    dispatch({ type: CLEAR_RELOAD });
  };

  return (
    <VideosContext.Provider
      value={{
        ...state,
        postVideo,
        getVideos,
        clearReload,
        createVideo,
        clearVideos,
        deleteVideo,
        getAllVideos,
        getFavoritos,
        clearFavoritos,
        getSingleVideo,
        agregarFavorito,
        eliminarFavorito,
        setPropiedadVideo,
      }}
    >
      {children}
    </VideosContext.Provider>
  );
};
